import { OBJECT_STATUS } from 'Data/Enum';
import Eventer from 'Lib/Eventer';
import queryString from 'query-string';
import { downloadBlob } from 'Util';
import { del, get, getResponseErrorPayload, post, put } from './SystemREST';

// Generic Module Event Bus. Handles generic interactions with any table.
const getTableDataBus = (busIdentifier, tableName) => {
  let tableDataBus = Eventer.createBus(busIdentifier);

  // Options in the form of:
  //  {
  //    _page: 1,
  //    _pageSize: 100,
  //    _sort: 'fieldname$[ASC|DESC]' (defaults to asc if order not provided)
  //    _include: 'associated-child-table-name,another-child-table-name'
  //    where: { '*fieldname': 'SEARCHTERM' }, SEARCHTERM can be '$NULL$' or '$NULL!' or any other string,
  //            use  '!SEARCHTERM' for not like/equal
  //            use  '~SEARCHTERM' for searching for a substring within a numerical field to be cast as text
  //  };
  tableDataBus.getRecordList = (
    options = {},
    includeAssociations = false,
    busListenerActionName = 'LIST_LOADED',
  ) => {
    let queryOptions = {};

    queryOptions = (({ _page, _pageSize, _sort, _include, where }) => {
      return {
        _page,
        _pageSize,
        _sort,
        _include,
        ...(includeAssociations && { include_associations: true }),
        ...{ '*id': '!0', ...where },
      };
    })(options);

    let requestURL = `api/${tableName}`;
    if (queryString.stringify(queryOptions).length > 0) {
      requestURL = `${requestURL}?${queryString.stringify(queryOptions)}`;
    }

    return get(requestURL)
      .send()
      .then((res) => {
        const payload = res.body;
        let results = { records: payload.data, count: payload.count };
        tableDataBus.notifyListeners(busListenerActionName, results);
        return Promise.resolve(results);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('LIST_LOAD_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to retrieve list`,
          debug: err,
        });
      });
  };

  // Options in the form of:
  //  {
  //    _page: 1,
  //    _pageSize: 100,
  //    _sort: 'fieldname$[ASC|DESC]' (defaults to asc if order not provided)
  //    _include: 'associated-child-table-name,another-child-table-name'
  //    where: { '*fieldname': 'SEARCHTERM' }, SEARCHTERM can be '$NULL$' or '$NULL!' or any other string
  //  };
  tableDataBus.exportRecordList = (
    options = {},
    busListenerActionName = 'LIST_EXPORTED',
  ) => {
    let queryOptions = {};

    queryOptions = (({ _sort, _include, where }) => {
      return {
        _sort,
        _include,
        ...{ '*id': '!0', ...where },
      };
    })(options);

    let requestURL = `api/${tableName}/export`;
    if (queryString.stringify(queryOptions).length > 0) {
      requestURL = `${requestURL}?${queryString.stringify(queryOptions)}`;
    }

    return post(requestURL)
      .send(options.columns && { columns: options.columns })
      .then((res) => {
        if (res.statusCode === 200) {
          downloadBlob(res, `${tableName}.csv`);
          tableDataBus.notifyListeners(busListenerActionName, res.text);
          return Promise.resolve(res.text);
        } else if (res.statusCode === 204) {
          console.log('No Data to Download');
          return Promise.resolve(res);
        }
      })
      .catch((err) => {
        tableDataBus.notifyListeners('LIST_EXPORT_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to export list`,
          debug: err,
        });
      });
  };

  tableDataBus.getAllRecords = ({
    fields = 'name',
    sort,
    excludePrimaryKey = false,
    busListenerActionName = 'ALL_RECORDS_LOADED',
  }) => {
    let queryOptions = {
      ...(sort && { _sort: `${sort}` }),
      _fields: fields,
      ...(excludePrimaryKey && { _exclude_primary_key: true }),
    };

    let requestURL = `api/${tableName}/all`;

    return get(requestURL)
      .query(queryOptions)
      .send()
      .then((res) => {
        const payload = res.body;
        let results = { records: payload.data, count: payload.count };

        tableDataBus.notifyListeners(busListenerActionName, results);
        return Promise.resolve(results);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('LIST_LOAD_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to retrieve list`,
        });
      });
  };

  tableDataBus.getBlank = () => {
    let requestURL = `api/${tableName}/null`;
    return get(requestURL)
      .send()
      .then((res) => {
        let payload = res.body;
        tableDataBus.notifyListeners('BLANK_RECORD_LOADED', payload.data);
        return Promise.resolve(payload.data);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('BLANK_RECORD_LOAD_ERROR', {
          ...getResponseErrorPayload(err),
          message: 'Unable to return a blank record',
        });
      });
  };

  tableDataBus.getRecord = (
    id,
    queryOptions = {},
    includeAssociations = false,
    busListenerActionName = 'RECORD_LOADED',
  ) => {
    queryOptions = {
      ...queryOptions,
      ...(includeAssociations && { include_associations: true }),
    };
    let requestURL = `api/${tableName}/${id}`;

    return get(requestURL)
      .query(queryOptions)
      .send()
      .then((res) => {
        let payload = res.body;
        tableDataBus.notifyListeners(busListenerActionName, payload.data);
        return Promise.resolve(payload.data);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORD_LOAD_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to find record with id: ${id}`,
        });
      });
  };

  tableDataBus.getRecords = (ids, includeAssociations = false) => {
    let requestURL = `api/${tableName}/${ids}${
      includeAssociations ? `?include_associations=true` : ''
    }`;
    return get(requestURL)
      .send()
      .then((res) => {
        const payload = res.body;
        const resultArray = ids.length > 1 ? payload.data : [payload.data];
        tableDataBus.notifyListeners('RECORDS_LOADED', resultArray);
        return Promise.resolve(resultArray);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORDS_LOAD_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to find records for ids: ${ids}`,
        });
      });
  };

  tableDataBus.createRecord = (
    newRecordData,
    busListenerActionName = 'RECORD_CREATED',
  ) => {
    let requestURL = `api/${tableName}`;
    return post(requestURL)
      .send({ data: newRecordData })
      .then((res) => {
        let payload = res.body;
        tableDataBus.notifyListeners(busListenerActionName, payload.data);
        return Promise.resolve(payload.data);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORD_CREATE_ERROR', {
          ...getResponseErrorPayload(err),
          message: 'Unable to create new record.',
        });
      });
  };

  tableDataBus.activateRecord = (id) => {
    return (
      id &&
      tableDataBus.updateRecord(
        { id, status: OBJECT_STATUS.ACTIVE.key },
        'RECORD_ACTIVATED',
      )
    );
  };

  tableDataBus.deactivateRecord = (id) => {
    return (
      id &&
      tableDataBus.updateRecord(
        { id, status: OBJECT_STATUS.DEACTIVATED.key },
        'RECORD_DEACTIVATED',
      )
    );
  };

  tableDataBus.updateRecord = (
    recordData,
    busListenerActionName = 'RECORD_UPDATED',
  ) => {
    let requestURL = `api/${tableName}`;
    return put(requestURL)
      .type('json')
      .send({ data: recordData })
      .then((res) => {
        let payload = res.body;
        tableDataBus.notifyListeners(busListenerActionName, payload.data);
        return Promise.resolve(payload.data);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORD_UPDATE_ERROR', {
          ...getResponseErrorPayload(err),
          message: 'Unable to create new record.',
        });
      });
  };

  tableDataBus.updateOrCreateRecords = (
    recordDataList,
    busListenerActionName = 'RECORDS_UPDATED',
  ) => {
    let requestURL = `api/${tableName}`;
    let promises = recordDataList.map((recordData) => {
      return new Promise((resolve, reject) => {
        if (recordData.id) {
          put(requestURL)
            .type('json')
            .send({ data: recordData })
            .then(
              (res) => {
                let payload = res.body;
                resolve(payload.data);
              },
              (err) => {
                reject(err);
              },
            );
        } else {
          post(requestURL)
            .type('json')
            .send({ data: recordData })
            .then(
              (res) => {
                let payload = res.body;
                resolve(payload.data);
              },
              (err) => {
                reject(err);
              },
            );
        }
      });
    });
    return Promise.all(promises)
      .then((results) => {
        tableDataBus.notifyListeners(busListenerActionName, results);
        return Promise.resolve(results);
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORDS_UPDATE_ERROR', {
          ...getResponseErrorPayload(err),
          message: 'Unable to update records.',
        });
      });
  };

  tableDataBus.deleteRecords = (ids) => {
    let requestURL = `api/${tableName}/${ids}`;
    return del(requestURL)
      .send()
      .then((res) => {
        tableDataBus.notifyListeners('RECORDS_DELETED', {
          message: `Records with ids: ${ids} deleted.`,
          ids: ids,
        });
        return Promise.resolve({
          message: `Records with ids: ${ids} deleted.`,
          deletedIds: ids,
        });
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORDS_DELETED_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to delete records for ids: ${ids}`,
        });
      });
  };

  tableDataBus.deleteRecordsWhere = (whereClause) => {
    let requestURL = `api/${tableName}`;
    return del(requestURL)
      .send({ whereClause: whereClause })
      .then((res) => {
        let payload = res.body;
        tableDataBus.notifyListeners('RECORDS_DELETED', {
          message: `${payload.data.count} records deleted.`,
          whereClause,
        });
        return Promise.resolve({
          message: `${payload.data.count} records deleted.`,
          whereClause,
        });
      })
      .catch((err) => {
        tableDataBus.notifyListeners('RECORDS_DELETED_ERROR', {
          ...getResponseErrorPayload(err),
          message: `Unable to delete records.`,
        });
      });
  };

  return tableDataBus;
};

export default {
  getTableDataBus,
};
